/**
 * Κεφαλίδα του επεξεργαστή καταλόγου (§13.11): επιλογέας πλατφόρμας, εισαγωγή με
 * πρόοδο ανά κατάστημα, τσιπ ηλικίας εισαγωγής, πλακίδιο καταστημάτων χωρίς
 * διαπιστευτήρια και το μόνιμο banner «αυτός ο server δεν στέλνει αλλαγές».
 *
 * ΓΙΑΤΙ τα τσιπ ηλικίας είναι κόκκινα μόνο με εκκρεμείς αλλαγές: το παράθυρο των
 * 10΄ αφορά το pre-image της ΑΠΟΣΤΟΛΗΣ (§13.6.3). Χωρίς εκκρεμείς αλλαγές μια
 * εισαγωγή δύο ωρών δεν βλάπτει κανέναν — το να κοκκινίζει ολόκληρη η σειρά χωρίς
 * λόγο εκπαιδεύει τον χειριστή να αγνοεί το κόκκινο.
 */

import React from 'react';
import {
  AlertTriangle, CloudOff, Download, KeyRound, Loader2, RefreshCw, Store,
} from 'lucide-react';
import {
  CATALOG_PLATFORMS, CatalogSummary, IMPORT_FRESH_MINUTES, PlatformName, StoreSummary,
} from '../../services/platformCatalogApi';
import { fmtAge, fmtDateTime, storeShort } from './catalogUi';

export interface ImportProgress {
  total: number;
  done: number;
  /** Το κατάστημα που τρέχει τώρα — η πρόοδος είναι ΑΝΑ ΚΑΤΑΣΤΗΜΑ, σειριακά. */
  current: string | null;
  failed: number;
}

interface Props {
  platform: PlatformName;
  onPlatformChange: (p: PlatformName) => void;
  summary: CatalogSummary | null;
  loading: boolean;
  canImport: boolean;
  importing: boolean;
  progress: ImportProgress | null;
  /** Υπάρχουν πρόχειρες αλλαγές κάπου στον οργανισμό. */
  hasOpenEdits: boolean;
  onImportAll: () => void;
  onImportStore: (storeId: number) => void;
  onRefresh: () => void;
}

const chipClasses = (store: StoreSummary, hasOpenEdits: boolean): string => {
  if (!store.has_credentials) return 'bg-red-500/10 border-red-300 dark:border-red-700/50 text-red-700 dark:text-red-300';
  if (store.last_import_at === null) return 'bg-surface-2 border-line-strong text-t3';
  const age = store.import_age_minutes;
  const stale = age !== null && age !== undefined && age > IMPORT_FRESH_MINUTES;
  if (stale && (hasOpenEdits || store.open_edits > 0)) {
    return 'bg-red-500/10 border-red-300 dark:border-red-700/50 text-red-700 dark:text-red-300';
  }
  if (stale) return 'bg-surface-2 border-line-strong text-t2';
  return 'bg-emerald-500/10 border-emerald-300 dark:border-emerald-700/50 text-emerald-700 dark:text-emerald-300';
};

const CatalogHeader: React.FC<Props> = ({
  platform, onPlatformChange, summary, loading, canImport, importing, progress,
  hasOpenEdits, onImportAll, onImportStore, onRefresh,
}) => {
  const stores = summary ? summary.stores : [];
  const withCreds = stores.filter(s => s.has_credentials);
  const without = stores.filter(s => !s.has_credentials);
  const totals = summary ? summary.totals : null;

  return (
    <div className="space-y-4">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="min-w-0">
          <h3 className="text-lg font-semibold text-t1 flex items-center gap-2">
            <Store className="w-5 h-5 text-blue-600 dark:text-blue-400" /> Κατάλογος πλατφορμών
          </h3>
          <p className="text-sm text-t3 mt-1 max-w-3xl">
            Εδώ βλέπεις και επεξεργάζεσαι τον κατάλογο <strong>της πλατφόρμας</strong> για όλη την αλυσίδα:
            εισάγουμε ό,τι έχει ήδη το κάθε κατάστημα στο efood, το συγκρίνουμε σε ένα πλέγμα και
            ετοιμάζουμε τις αλλαγές. Οι τιμές του efood <strong>δεν</strong> είναι οι τιμές του POS —
            τίποτα δεν δημοσιεύεται από τον κατάλογό μας.
          </p>
        </div>

        <div className="flex items-center gap-2 flex-wrap shrink-0">
          <select
            value={platform}
            onChange={e => onPlatformChange(e.target.value as PlatformName)}
            className="bg-surface-0 border border-line-strong rounded-lg px-3 py-2 text-sm text-t1"
            aria-label="Πλατφόρμα"
          >
            {CATALOG_PLATFORMS.map(p => (
              <option key={p.value} value={p.value} disabled={!p.enabled}>
                {p.label}{p.enabled ? '' : ' (σύντομα)'}
              </option>
            ))}
          </select>

          <button
            type="button"
            onClick={onRefresh}
            disabled={loading || importing}
            className="px-3 py-2 text-sm bg-surface-2 hover:bg-surface-3 disabled:opacity-50 text-t1 border border-line-strong rounded-xl flex items-center gap-2"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} /> Ανανέωση
          </button>

          <button
            type="button"
            onClick={onImportAll}
            disabled={!canImport || importing || withCreds.length === 0}
            title={withCreds.length === 0
              ? 'Κανένα κατάστημα με διαπιστευτήρια efood'
              : 'Διαβάζει τον κατάλογο κάθε καταστήματος από το efood (μόνο ανάγνωση)'}
            className="px-3.5 py-2 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white rounded-xl text-sm font-medium flex items-center gap-2"
          >
            {importing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
            Εισαγωγή από efood
          </button>
        </div>
      </div>

      {/* Πρόοδος εισαγωγής — ανά κατάστημα, γιατί σειριακά τρέχει (ένα advisory
          lock ανά κατάστημα) και ο χειριστής θέλει να ξέρει πού βρίσκεται. */}
      {importing && progress && (
        <div className="p-3 rounded-xl border border-blue-300 dark:border-blue-700/50 bg-blue-50 dark:bg-blue-900/20">
          <div className="flex items-center justify-between text-sm text-blue-900 dark:text-blue-200">
            <span className="flex items-center gap-2 min-w-0">
              <Loader2 className="w-4 h-4 animate-spin shrink-0" />
              <span className="truncate">
                Εισαγωγή {progress.done}/{progress.total}
                {progress.current ? ` — ${progress.current}` : ''}
              </span>
            </span>
            {progress.failed > 0 && (
              <span className="text-red-700 dark:text-red-300 shrink-0">{progress.failed} απέτυχαν</span>
            )}
          </div>
          <div className="mt-2 h-1.5 rounded-full bg-blue-200/60 dark:bg-blue-800/60 overflow-hidden">
            <div
              className="h-full bg-blue-600 transition-all"
              style={{ width: `${progress.total > 0 ? Math.round((progress.done / progress.total) * 100) : 0}%` }}
            />
          </div>
        </div>
      )}

      {/* ΜΟΝΙΜΟ banner: ο server δεν είναι εκδότης (§13.8.1, πύλες 1-3). Δεν είναι
          σφάλμα — είναι η κανονική κατάσταση σε κάθε server εκτός του εκδότη. */}
      {summary && !summary.server_can_push && (
        <div className="p-3 rounded-xl text-sm flex items-start gap-2 border bg-surface-1/60 border-line-strong text-t2">
          <CloudOff className="w-4 h-4 mt-0.5 shrink-0 text-t3" />
          <span className="min-w-0">
            Αυτός ο server <strong>δεν στέλνει</strong> αλλαγές στο efood — μπορείς να επεξεργαστείς και
            να κάνεις δοκιμαστική εκτέλεση. Η αποστολή γίνεται μόνο από τον server που έχει άδεια εγγραφής.
          </span>
        </div>
      )}

      {/* Καταστήματα χωρίς διαπιστευτήρια: δεν εισάγονται ποτέ και ΔΕΝ πρέπει να
          μοιάζουν με «άδειο κατάλογο». */}
      {without.length > 0 && (
        <div className="p-3 rounded-xl border border-red-300 dark:border-red-700/50 bg-red-50 dark:bg-red-900/20">
          <div className="flex items-center gap-2 text-sm font-medium text-red-800 dark:text-red-200">
            <KeyRound className="w-4 h-4 shrink-0" />
            {without.length} {without.length === 1 ? 'κατάστημα' : 'καταστήματα'} χωρίς διαπιστευτήρια efood
          </div>
          <p className="text-xs text-red-700/80 dark:text-red-300/80 mt-1">
            Δεν εισάγονται και δεν εμφανίζονται ως στήλες στο πλέγμα. Οι κωδικοί μπαίνουν από τις
            ρυθμίσεις πλατφορμών του κάθε καταστήματος.
          </p>
          <div className="flex flex-wrap gap-1.5 mt-2">
            {without.map(s => (
              <span key={s.store_id} className="px-2 py-0.5 rounded-lg text-[11px] bg-red-500/15 text-red-700 dark:text-red-300">
                {s.store_name}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Τσιπ ηλικίας εισαγωγής ανά κατάστημα. */}
      {withCreds.length > 0 && (
        <div className="flex flex-wrap gap-1.5 items-center">
          <span className="text-xs text-t3 mr-1">Ηλικία εισαγωγής:</span>
          {withCreds.map(s => {
            const age = s.import_age_minutes;
            const stale = age !== null && age !== undefined && age > IMPORT_FRESH_MINUTES;
            return (
              <button
                key={s.store_id}
                type="button"
                onClick={() => canImport && !importing && onImportStore(s.store_id)}
                disabled={!canImport || importing}
                title={`${s.store_name}\nΤελευταία εισαγωγή: ${fmtDateTime(s.last_import_at)}`
                  + `\nΕίδη: ${s.items} · Μη διαθέσιμα: ${s.unavailable} · Εκκρεμείς αλλαγές: ${s.open_edits}`
                  + (stale && (hasOpenEdits || s.open_edits > 0)
                    ? `\nΠάνω από ${IMPORT_FRESH_MINUTES}΄ με εκκρεμείς αλλαγές — χρειάζεται νέα εισαγωγή πριν από αποστολή.`
                    : '')
                  + (canImport ? '\nΚλικ: νέα εισαγωγή μόνο αυτού του καταστήματος.' : '')}
                className={`px-2 py-1 rounded-lg text-[11px] border flex items-center gap-1 disabled:cursor-default ${chipClasses(s, hasOpenEdits)}`}
              >
                <span className="font-medium">{storeShort(s.store_name)}</span>
                <span className="opacity-80">{fmtAge(s.import_age_minutes)}</span>
                {s.open_edits > 0 && (
                  <span className="px-1 rounded bg-amber-500/25 text-amber-800 dark:text-amber-200">{s.open_edits}</span>
                )}
              </button>
            );
          })}
        </div>
      )}

      {totals && (
        <div className="flex flex-wrap gap-4 text-xs text-t3">
          <span>Καταστήματα: <strong className="text-t2">{totals.stores}</strong></span>
          <span>Με διαπιστευτήρια: <strong className="text-t2">{totals.with_credentials}</strong></span>
          <span>Με εισαγωγή: <strong className="text-t2">{totals.imported}</strong></span>
          <span>Είδη αλυσίδας: <strong className="text-t2">{totals.masters}</strong></span>
          <span>Εκκρεμείς αλλαγές: <strong className="text-t2">{totals.open_edits}</strong></span>
          {totals.conflicts > 0 && (
            <span className="text-red-600 dark:text-red-400 flex items-center gap-1">
              <AlertTriangle className="w-3.5 h-3.5" /> Συγκρούσεις: <strong>{totals.conflicts}</strong>
            </span>
          )}
        </div>
      )}
    </div>
  );
};

export default CatalogHeader;
